import React, { useEffect } from 'react';
import useRouter from 'use-react-router';
import { useSelector } from 'react-redux';
import * as queryString from 'query-string';
import Spinner from '../../../../../common/components/Spinner';
import { getDeviceInfo } from '../../../../../common/hooks/use-device-info';
import BotPanelHeader from '../../../../common/BotPanelHeader/BotPanelHeader';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { StartBotPromoterTable } from './StartBotPromoterTable/StartBotPromoterTable';
import StartBotRecommendations from './StartBotRecommendations/StartBotRecommendations';
import EmptyPageBacksplash from '../../../../common/EmptyPageBacksplash/EmptyPageBacksplash';
import CustomGraph from '../../../../components/CustomGraph/CustomGraph';
import { GraphSeries } from '../../../../components/CustomGraph/constants';
import { useStartBotDetails } from '../use-startbot-details';
import { StoreState } from '@mm/state/store-interfaces';
import { useSafeState } from '../../../../../common/hooks/use-safe-state';
import { useCreateStartBotPromoterByType } from '../use-create-start-bot-promoter-by-type';
import './StartBotPromoterPanel.pcss';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { LinkTypes, PromoterTypes } from '@mm/state/model/PromoterModel';
import { selectPromotersByType } from '@mm/state/selectors/promoterSelector';
import { PlusCircleIcon } from '@mm/icons';

type Props = {
  kind: LinkTypes;
};

const shouldRenderRightButton = (
  kind: LinkTypes,
  startBotsLength: number,
  isFetching: boolean,
) => {
  if (isFetching) return false;

  switch (kind) {
    case 'customer_chat':
      return startBotsLength > 0;
    case 'fb_comment_guard':
      return true;
    case 'optin':
      return true;
    default:
      throw new Error('Hanlde the case of new kind');
  }
};

const useHandlersAndPrimeState = (promoterKind: LinkTypes) => {
  const { history } = useRouter();
  const activeChatbot = useSelector(
    (state: StoreState) => state.chatbot.activeChatbot,
  );
  const promoters = useSelector((state: StoreState) =>
    selectPromotersByType(state, promoterKind),
  );
  const promoterCRUDActions = usePromoterCRUDActions();
  const [isFetching, setIsFetching] = useSafeState(true);
  const startBotDetails = useStartBotDetails(promoterKind);
  const createStartBotPromoter = useCreateStartBotPromoterByType(promoterKind);

  const onChangePage = async (page: number) => {
    const queryParams = queryString.stringify({ pagingNumber: page });

    if (activeChatbot) {
      setIsFetching(true);
      await promoterCRUDActions.retrieveAll(activeChatbot.id, promoterKind);
      setIsFetching(false);

      history.push({
        pathname: startBotDetails.routes.index.getLink({
          chatbotId: activeChatbot.id,
        }),
        search: queryParams,
      });
    }
  };

  useEffect(() => {
    const primeState = async () => {
      if (activeChatbot) {
        await promoterCRUDActions.retrieveAll(activeChatbot.id, promoterKind);

        setIsFetching(false);
      }
    };

    primeState();
  }, []);

  return {
    isFetching,
    onChangePage,
    createStartBotPromoter,
    promoters,
  };
};

export const StartBotPromoterPanel = (props: Props) => {
  const { kind } = props;
  const startBotDetails = useStartBotDetails(kind);
  const { isFetching, onChangePage, createStartBotPromoter, promoters } =
    useHandlersAndPrimeState(kind);
  const { isMobile } = getDeviceInfo();

  return (
    <div className="start-bot-promoter-panel">
      <Spinner show={isFetching} position="absolute" />

      <BotPanelHeader
        featureGroupName={startBotDetails.featureGroupName}
        panelName={startBotDetails.promoterName}
        tipInfo={startBotDetails.tipInfo}
        rightPanelContent={
          <div>
            {shouldRenderRightButton(kind, promoters.length, isFetching) && (
              <PrimaryButton
                onClick={() => createStartBotPromoter()}
                className="start-bot-promoter-panel__add-start-bot"
              >
                Add {startBotDetails.singularPromoterName}
              </PrimaryButton>
            )}
          </div>
        }
      />

      {kind === PromoterTypes.FB_COMMENT_GUARD && !isMobile && (
        <div className="start-bot-promoter-panel__graph">
          <div className="start-bot-promoter-panel__graph-title">
            Page Posts Comments and New Leads
          </div>
          <CustomGraph
            dataSeries={[
              GraphSeries.commentGuardContacts,
              GraphSeries.commentsCount,
            ]}
          />
        </div>
      )}
      {!isMobile && kind === PromoterTypes.FB_COMMENT_GUARD && (
        <StartBotRecommendations />
      )}

      {!isFetching && (
        <>
          {promoters.length > 0 ? (
            <StartBotPromoterTable
              kind={kind}
              promoters={promoters}
              onChangePage={onChangePage}
              createStartBot={createStartBotPromoter}
              singularName={startBotDetails.singularPromoterName}
            />
          ) : (
            <EmptyPageBacksplash pageIcon={startBotDetails.noPromoterImage}>
              <PrimaryButton
                leftIcon={<PlusCircleIcon />}
                onClick={() => createStartBotPromoter()}
              >
                New {startBotDetails.singularPromoterName}
              </PrimaryButton>
            </EmptyPageBacksplash>
          )}
        </>
      )}
    </div>
  );
};
